import { readCookie } from '../helpers/cookie.helper';

declare const et_eC_Wrapper;
declare const et_vm_reload;
declare const ET_Event;

/**
 * All the here defined functions and objects are call from other JS files from SEW.
 * With the old tracking from common.js they were provided global.
 * So that the old function calls continue to work, we have to provide them global for JS to
 */
declare global {
    interface Window {
        initTrackingTriggers: (element: Element) => void;
        SEW: Record<string, unknown>;
        // this funcion is called from master_os.js. It's not found in any of our repos.
        dg_et_fireCcEvent: (
            cc_AppNameValue: string,
            _cc_AppNamePlainValue: string,
            cc_AppStepValue: string,
            _cc_AppStepPlainValue: string
        ) => void;
        et_cc_on_ready: (element: unknown) => void;
        et_seg1?: string;
        et_seg1_store?: string;
        // values are set for global javascript context in CMS
        et_seg2?: string;
        et_seg3?: string;
        path?: string;
    }
}

export class Tracking {
    private static instance: Tracking;
    private dataSecureCode: string;
    private isEtrackerActivated = false;
    private userGuid = '';
    private alreadyTrackedNodes: (Element | HTMLElement)[] = [];

    private allowedEtrackerEventTypes = ['click', 'mousedown', 'mouseover'];

    private constructor(
        useCMP: boolean,
        dataSecureCode: string,
        eTrackerCookiesLifetimeDays: number | null,
        isEtrackerActivated: boolean
    ) {
        this.dataSecureCode = dataSecureCode;
        this.isEtrackerActivated = isEtrackerActivated;

        this.initMutationObserver();
        this.loadETrackerScript(useCMP, eTrackerCookiesLifetimeDays);

        this.trackPageLoadForSap();
    }

    public static getInstance(
        useCMP: boolean,
        dataSecureCode: string,
        eTrackerCookiesLifetimeDays: number | null,
        isEtrackerActivated: boolean
    ): Tracking {
        if (!Tracking.instance) {
            Tracking.instance = new Tracking(
                useCMP,
                dataSecureCode,
                eTrackerCookiesLifetimeDays,
                isEtrackerActivated
            );
        }

        return Tracking.instance;
    }

    private initMutationObserver() {
        const observer = new MutationObserver(mutations => {
            // fired when a mutation occurs
            for (const mutation of mutations) {
                mutation.addedNodes.forEach((addedNode: Node) => {
                    if (!(addedNode instanceof Element || addedNode instanceof HTMLElement)) {
                        return;
                    }
                    // E-Tracker Event Tracking
                    let elements = this.selectorAllWithShadowDom(
                        node => node.hasAttribute('data-sew-trackme-event-category'),
                        addedNode
                    );
                    elements.forEach(element => {
                        this.applyETrackerTrackEventOnElement(element);
                    });
                    // SEW Tracker and E-Tracker Journey (Standard)-Tracking
                    elements = this.selectorAllWithShadowDom(
                        node => node.classList.contains('js-dg-et-cc-trigger'),
                        addedNode
                    );
                    elements.forEach(element => {
                        this.applyOnClickTrackEventOnElement(element);
                    });
                });
            }
        });

        observer.observe(document, {
            subtree: true,
            childList: true,
        });
    }

    private loadETrackerScript(useCMP = false, eTrackerCookiesLifetimeDays: number | null) {
        const script = document.createElement('script');
        script.id = '_etLoader';
        script.type = 'text/javascript';
        script.setAttribute('charset', 'UTF-8');
        script.setAttribute('data-secure-code', this.dataSecureCode);
        if (eTrackerCookiesLifetimeDays) {
            script.setAttribute('data-cookie-lifetime', eTrackerCookiesLifetimeDays.toString());
        }
        script.src = 'https://static.etracker.com/code/e.js';

        script.onload = () => {
            this.initOnClickTracking();
            this.initETrackerEventTracking();
        };

        if (useCMP !== false) {
            script.setAttribute('data-cmp-vendor', 's987');
            script.setAttribute('data-block-cookies', 'true');
            script.setAttribute('data-cmp-ab', '1');
            script.setAttribute('data-respect-dnt', 'true');
        }
        document.getElementsByTagName('head')[0].appendChild(script);
        window.initTrackingTriggers = this.initETrackerEventTrackingForElementsChildren;
        window.SEW = {
            et_vm_reloadSewWrapper: this.etVmReloadSewWrapper,
            et_cc_fire_event: this.et_cc_fire_event,
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateETracker: function () {},
        };
        window.dg_et_fireCcEvent = this.dg_et_fireCcEvent;
        window.et_cc_on_ready = element => {
            if (!element || !(element instanceof HTMLElement)) {
                return;
            }

            const elements = element.querySelectorAll('.js-dg-et-cc-ready');
            elements.forEach(element => {
                const app_name = element.getAttribute('data-dg-et-app-name');
                const app_step = element.getAttribute('data-dg-et-app-step');
                const app_name_plain = element.getAttribute('data-dg-et-app-plain-name');
                const app_step_plain = element.getAttribute('data-dg-et-app-plain-step');

                if (app_name && app_step && app_name_plain && app_step_plain) {
                    this.dg_et_fireCcEvent(app_name, app_name_plain, app_step, app_step_plain);
                }
            });
        };
    }

    public setUserGuid(userGuid: string): void {
        this.userGuid = userGuid;
        if (readCookie('et_allow_cookies') === '1') {
            window.et_seg1 = userGuid;
            window.et_seg1_store = userGuid;
        }
    }

    public trackETrackerEvent(element: Element): void {
        if (!this.isEtrackerActivated || typeof ET_Event === 'undefined') {
            return;
        }

        const category = element.getAttribute('data-sew-trackme-event-category');
        const object = element.getAttribute('data-sew-trackme-event-object');
        let action = element.getAttribute('data-sew-trackme-event-action');

        if (action === '' || action === null) {
            action = 'Click';
        }

        // etracking-Lib from javascript
        ET_Event.eventStart(category, object, action, '');
    }

    public startTracking = (appName: string, appStep: string, appClick?: string, appNav?: string) => {
        if (!appName || !appStep) {
            return;
        }
        if (this.isEtrackerActivated) {
            this.sendEtrackerWrapperEvent(appName, appStep, appClick, appNav);
        }
    };

    private trackPageLoadForSap() {
        const elements = document.getElementsByClassName('js-dg-et-cc-ready');
        if (elements.length < 1) {
            return;
        }

        const element = elements[0];
        const appName = element.getAttribute('data-dg-et-app-name');
        const appStep = element.getAttribute('data-dg-et-app-step');
        const appClick = element.getAttribute('data-dg-et-app-click');
        const appNav = element.getAttribute('data-dg-et-app-nav');

        this.sendEtrackerWrapperEvent(appName, appStep, appClick, appNav);
    }

    private initOnClickTracking() {
        const elements = document.getElementsByClassName('js-dg-et-cc-trigger');
        Array.from(elements).forEach(element => this.applyOnClickTrackEventOnElement(element));
    }

    private applyOnClickTrackEventOnElement(element: Element) {
        if (this.alreadyTrackedNodes.find(node => node === element) !== undefined) {
            return;
        }
        this.alreadyTrackedNodes.push(element);
        const appName = element.getAttribute('data-dg-et-app-name');
        const appStep = element.getAttribute('data-dg-et-app-step');
        const appClick = element.getAttribute('data-dg-et-app-click');
        const appNav = element.getAttribute('data-dg-et-app-nav');

        element.addEventListener('mousedown', () => {
            this.startTracking(appName, appStep, appClick, appNav);
        });
    }

    private initETrackerEventTracking() {
        const elements = this.selectorAllWithShadowDom(node =>
            node.hasAttribute('data-sew-trackme-event-category')
        );
        elements.forEach(element => {
            this.applyETrackerTrackEventOnElement(element);
        });
    }

    private initETrackerEventTrackingForElementsChildren(element: Element) {
        if (element.hasAttribute('data-sew-trackme-event-category')) {
            this.applyETrackerTrackEventOnElement(element);
        }
        const childElements = element.querySelectorAll(
            '[data-sew-trackme-event-category]:not([data-sew-trackme-event-category=""])'
        );
        Array.from(childElements).forEach(childElement => {
            this.applyETrackerTrackEventOnElement(childElement);
        });
    }

    private applyETrackerTrackEventOnElement(element: Element) {
        if (this.alreadyTrackedNodes.find(node => node === element) !== undefined) {
            return;
        }
        this.alreadyTrackedNodes.push(element);
        let eventType = element.getAttribute('data-sew-trackme-js-trigger-type');
        eventType = this.checkAllowdEventTypes(eventType);
        element.addEventListener(eventType, () => this.trackETrackerEvent(element));
    }

    private checkAllowdEventTypes(event: string) {
        if (!(this.allowedEtrackerEventTypes.indexOf(event) > -1)) {
            return 'mousedown';
        } else {
            return event;
        }
    }

    private sendEtrackerWrapperEvent(appName: string, appStep: string, appClick?: string, appNav?: string) {
        if (typeof et_eC_Wrapper !== 'function') {
            this.waitForEtWrapper(() => this.sendEtrackerWrapperEvent(appName, appStep, appClick, appNav));

            return;
        }

        let et_seg1 = '';
        const et_seg2 = appName;
        const et_seg3 = appStep;
        // supply string for optional parameters
        const et_seg4 = appClick ?? '';
        const et_seg5 = appNav ?? '';

        if (readCookie('et_allow_cookies') === '1') {
            et_seg1 = this.userGuid;
        }

        et_eC_Wrapper({
            et_et: this.dataSecureCode,
            et_seg1,
            et_seg2,
            et_seg3,
            et_seg4,
            et_seg5,
        });
    }

    private waitForEtWrapper(callback: () => void) {
        if (typeof et_eC_Wrapper !== 'function') {
            setTimeout(() => this.waitForEtWrapper(callback), 300);

            return;
        }

        callback();
    }

    private etVmReloadSewWrapper() {
        if (typeof et_vm_reload === 'function') {
            et_vm_reload();
        }
    }

    /**
     * This function is called from other javascript. Unused parameters are needed to match the always
     * available calls.
     *
     * @param cc_AppNameValue
     * @param _cc_AppNamePlainValue
     * @param cc_AppStepValue
     * @param _cc_AppStepPlainValue
     */
    private dg_et_fireCcEvent = (
        cc_AppNameValue: string,
        _cc_AppNamePlainValue: string,
        cc_AppStepValue: string,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _cc_AppStepPlainValue: string
    ) => {
        this.startTracking(cc_AppNameValue, cc_AppStepValue);
    };

    private et_cc_fire_event = (array: unknown[]) => {
        const element: HTMLElement = array[0] as HTMLElement;
        if (element.hasAttribute('data-dg-et-app-name') && element.hasAttribute('data-dg-et-app-step')) {
            const appName = element.getAttribute('data-dg-et-app-name');
            const appStep = element.getAttribute('data-dg-et-app-step');
            const appClick = element.getAttribute('data-dg-et-app-click');
            const appNav = element.getAttribute('data-dg-et-app-nav');

            this.sendEtrackerWrapperEvent(appName, appStep, appClick, appNav);
        }
    };

    private selectorAllWithShadowDom = (
        selector: (node: HTMLElement | Element) => boolean,
        rootNode: HTMLElement | Element = document.documentElement
    ): (HTMLElement | Element)[] => {
        const arr = [];

        const traverser = (node: HTMLElement | Element) => {
            // 1. decline all nodes that are not elements
            if (node.nodeType !== Node.ELEMENT_NODE) {
                return;
            }

            // 2. add the node to the array, if it matches the selector
            if (selector(node)) {
                // add node only, if it's not already tracked, due to multiple calls
                if (this.alreadyTrackedNodes.find(trackedNode => node === trackedNode) === undefined) {
                    arr.push(node);
                }
            }

            // 3. loop through the children
            const children = node.children;
            if (children.length) {
                Array.from(children).forEach(function (child) {
                    traverser(child);
                });
            }

            // 4. check for shadow DOM, and loop through it's children
            const shadowRoot = node.shadowRoot;
            if (shadowRoot) {
                const shadowChildren = shadowRoot.children;
                Array.from(shadowChildren).forEach(function (shadowChild) {
                    traverser(shadowChild);
                });
            }
        };

        traverser(rootNode);

        return arr;
    };
}
